import { redirect } from "@tanstack/react-router";
import { authClient } from "@/lib/auth-client";
import { canSeeIntegrations, canSeeResourcesPage } from "@/lib/nav-access";
import { hasCapability } from "@/lib/permissions";

// ── Frontend session guards (used inside TanStack Router beforeLoad) ──────────

export async function requireAuth() {
  const { data, error } = await authClient.getSession();
  if (data) return data;
  // A transient get-session failure (network blip, 5xx, ECS task cycling) returns no
  // data WITH an error set — distinct from a clean "no session" (data null, error
  // null). Redirecting to /login on the former masquerades as a sign-out on refresh,
  // so we rethrow and let the ErrorBoundary render a retry instead of logging the
  // user out. Only a genuine absence of a session sends them to /login.
  if (error) {
    throw new Error(error.message ?? "Could not verify your session. Please try again.");
  }
  throw redirect({ to: "/login" });
}

/** ADMIN and RM have admin-plugin privileges (impersonate, user management, settings). */
export async function requireAdmin() {
  const session = await requireAuth();
  const role = session.user.role;
  if (role !== "ADMIN" && role !== "RM") throw redirect({ to: "/dashboard" });
  return session;
}

/**
 * Settings → Internal Rates (2026-10-14): ADMIN / FIN, and RM read-only since 2026-10-15
 * (writes stay behind manageResourceInternalRates). Everyone else is sent back to
 * Settings. The API refuses them regardless; this keeps the page from rendering at all.
 */
export async function requireInternalRatesAccess() {
  const session = await requireAuth();
  if (!hasCapability(session.user.role, "viewResourceInternalRates")) {
    throw redirect({ to: "/dashboard/settings" });
  }
  return session;
}

/**
 * The Resources page (2026-10-20): every role but SA. A Solution Architect reaching the
 * URL directly is sent to Solutions, the same quiet redirect as the other page guards.
 */
export async function requireResourcesPageAccess() {
  const session = await requireAuth();
  if (!canSeeResourcesPage(session.user.role)) throw redirect({ to: "/dashboard" });
  return session;
}

/** Integrations (2026-10-20): hidden from a role that can neither refresh nor import. */
export async function requireIntegrationsAccess() {
  const session = await requireAuth();
  if (!canSeeIntegrations(session.user.role)) throw redirect({ to: "/dashboard" });
  return session;
}

// Server-side capability enforcement (Actor, ScopeCheckContext, requireCapability)
// now lives in src/lib/permissions-server.ts to keep the frontend bundle clean of
// Postgres / Drizzle imports.
